import { useEffect, useState } from 'react';
import type { FunctionComponent, ReactNode, SVGProps } from 'react';
import { useParams } from 'react-router-dom';
import { Select, message } from 'antd';
import dayjs from 'dayjs';
import Breadcrumbs from '../../components/Breadcrumbs';
import CommentsPanel from '../../components/CommentsPanel';
import ActivityHistoryPanel from '../../components/ActivityHistoryPanel';
import {
  DirFieldIcon,
  DirCalendarIcon,
  FolderIcon,
  FileXlsxIcon,
  RefreshIcon,
  DdAssigneeIcon,
  DdClockIcon,
  DdDetailsIcon,
  DdReferenceIcon,
  DownloadIcon,
  TemplateDocIcon,
} from '../../assets/icons';
import {
  useGetDirectionQuery,
  useUpdateDirectionMutation,
  useGetReferencePersonsQuery,
  referencePersonName,
  dirCode,
  dirTitle,
  dirOneLine,
  dirDescription,
  dirPriority,
  dirRawStatus,
  dirDueDate,
  dirDueTime,
  dirDuration,
  dirReferencePerson,
  dirReferencePersonName,
  dirReferencePersonPhone,
} from '../../features/dailyDirections/dailyDirectionApi';
import { useGetMyTreeQuery } from '../../features/organizations/organizationsApi';
import { useGetEcTreeQuery, type EcTreeNode } from '../../features/territory/territoryApi';
import {
  useGetAttachmentsQuery,
  useGetAssigneesQuery,
  useCreateActivityLogMutation,
  attachmentName,
  assigneeName,
  type Attachment,
} from '../../features/activity/activityApi';
import { useAppSelector } from '../../app/hooks';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

const CARD = 'rounded-xl border border-[#e5e7eb] bg-white p-6 shadow-card';
const ENTITY = 'DailyDirection';

const PRIORITY_BADGE: Record<string, { cls: string; label: string }> = {
  high: { cls: 'bg-[#fee2e2] text-[#b91c1c]', label: 'High Priority' },
  critical: { cls: 'bg-[#fee2e2] text-[#b91c1c]', label: 'Critical' },
  medium: { cls: 'bg-[#eff6ff] text-brand', label: 'Medium Priority' },
  low: { cls: 'bg-[#f1f5f9] text-[#475569]', label: 'Low Priority' },
};

const STATUS_OPTIONS = [
  { value: 'To-Do', label: 'To-Do' },
  { value: 'Done', label: 'Done' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Doing', label: 'Doing' },
  { value: 'Discarded', label: 'Disposed Off' },
  { value: 'List to Be Maintained', label: 'List to Be Maintained' },
  { value: 'Listed', label: 'Listed' },
  { value: 'Listed In House', label: 'Listed In House' },
  { value: 'Discuss', label: 'Discuss' },
];
const DEFAULT_STATUS = STATUS_OPTIONS[0].value;

/** Reference-person scope key → display label. */
const REF_SCOPE_LABEL: Record<string, string> = {
  inside: 'Within Constituency',
  outside: 'Outside Constituency',
  delhi: 'NCR',
};

const ecNodeKey = (n: EcTreeNode) => n.code || n.name || '';

/** A labelled read-only value used in the detail cards. */
function InfoField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#6b7280]">{label}</span>
      <span className="text-sm text-ink">{children}</span>
    </div>
  );
}

function CardTitle({ Icon, children }: { Icon: IconType; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="size-5 text-brand" />
      <h2 className="text-lg font-bold text-ink">{children}</h2>
    </div>
  );
}

function Meta({ Icon, children }: { Icon: IconType; children: ReactNode }) {
  return (
    <span className="flex items-center gap-2 text-sm text-[#6b7280]">
      <Icon className="size-4" />
      {children}
    </span>
  );
}

/** Pick an icon + tint for a file by extension/type. */
function fileVisual(a: Attachment): { Icon: IconType; color: string } {
  const t = (a.file_type || attachmentName(a).split('.').pop() || '').toLowerCase();
  if (t.includes('xls') || t.includes('csv')) return { Icon: FileXlsxIcon, color: 'text-[#16a34a]' };
  return { Icon: TemplateDocIcon, color: 'text-[#ef4444]' };
}

/** True when the attachment is an image we can preview inline. */
function isImage(a: Attachment): boolean {
  const t = (a.file_type || attachmentName(a).split('.').pop() || '').toLowerCase();
  return /(^image)|jpe?g|png|gif|webp|bmp|svg|avif/.test(t);
}

export default function DailyDirectionStatusPage() {
  const { id } = useParams();
  const { data: direction, isFetching } = useGetDirectionQuery(id!, { skip: !id });
  const { data: attachments } = useGetAttachmentsQuery(
    { entityType: ENTITY, entityId: id! },
    { skip: !id },
  );
  const { data: assignees } = useGetAssigneesQuery(
    { entityType: ENTITY, entityId: id! },
    { skip: !id },
  );
  const [updateDirection, { isLoading: isSaving }] = useUpdateDirectionMutation();
  const [createActivityLog] = useCreateActivityLogMutation();
  const userId = useAppSelector((s) => s.auth.user?.id);

  const [status, setStatus] = useState(DEFAULT_STATUS);
  useEffect(() => {
    if (direction) setStatus(dirRawStatus(direction) || DEFAULT_STATUS);
  }, [direction]);

  const title = direction ? dirTitle(direction) : 'Daily Direction';
  const files = attachments ?? [];
  const assigneeNames = (assignees ?? []).map(assigneeName).filter(Boolean);

  // Reference person — resolve the person's name (by id) and the location name via org data.
  const rp = direction ? dirReferencePerson(direction) : null;
  const { data: myTree } = useGetMyTreeQuery();
  const org = myTree?.organization ?? undefined;
  const { data: refPersons } = useGetReferencePersonsQuery(
    { organization: org! },
    { skip: org == null || typeof rp?.user !== 'number' },
  );
  const { data: ecTree } = useGetEcTreeQuery(
    { organization: org!, booths: false },
    { skip: org == null || !rp?.location },
  );
  const refPersonLabel = (() => {
    // Prefer the resolved name from the get-one response.
    const fromKey = direction ? dirReferencePersonName(direction) : '';
    if (fromKey) return fromKey;
    if (!rp) return '—';
    if (rp.name) return rp.name; // outside / NCR → typed name
    if (typeof rp.user === 'string') return rp.user || '—'; // legacy
    if (typeof rp.user === 'number')
      return referencePersonName((refPersons ?? []).find((p) => p.id === rp.user) ?? { id: rp.user });
    return '—';
  })();
  const refLocationLabel = (() => {
    if (!rp?.location) return '';
    // Full breadcrumb path (root › … › node) for the selected location code.
    const labelPath: Record<string, string> = {};
    const walk = (n: EcTreeNode, anc: string[]) => {
      const k = ecNodeKey(n);
      const names = [...anc, n.name || k];
      if (k) labelPath[k] = names.filter(Boolean).join(' › ');
      n.children?.forEach((c) => walk(c, names));
    };
    (ecTree ?? []).forEach((n) => walk(n, []));
    return labelPath[rp.location] || rp.location;
  })();

  const submitStatus = async () => {
    if (!id) return;
    const prevStatus = direction ? dirRawStatus(direction) : '';
    try {
      await updateDirection({ id, patch: { status } }).unwrap();
      message.success('Status updated.');
      // Record the change in the Activity History (the DailyDirection API doesn't auto-log).
      // Fire-and-forget: the log mutation invalidates 'ActivityLog', so the panel refetches.
      if (userId && status !== prevStatus) {
        createActivityLog({
          entity_type: ENTITY,
          entity_id: Number(id),
          user: userId,
          action: 'status_changed',
          old_value: prevStatus || undefined,
          new_value: status,
        });
      }
    } catch {
      message.error('Could not update the status. Please try again.');
    }
  };

  const priorityKey = direction ? dirPriority(direction).toLowerCase() : '';
  const badge = PRIORITY_BADGE[priorityKey];
  const dueDate = direction ? dirDueDate(direction) : '';
  const dueTime = direction ? dirDueTime(direction) : '';
  const duration = direction ? dirDuration(direction) : '';

  return (
    <div className="flex flex-col gap-8">
      {/* Breadcrumb */}
      <Breadcrumbs
        items={[{ label: 'Daily Directions', to: '/user/directions' }, { label: title }]}
      />

      {/* Header */}
      <div className={[CARD, 'flex flex-col gap-4'].join(' ')}>
        <div className="flex flex-wrap items-center gap-3">
          {direction && (
            <span className="rounded-md bg-[#f3f4f6] px-2.5 py-1 text-xs font-semibold text-ink">
              {dirCode(direction)}
            </span>
          )}
          {badge && (
            <span className={['rounded-md px-2.5 py-1 text-xs font-bold uppercase', badge.cls].join(' ')}>
              {badge.label}
            </span>
          )}
          {status && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand px-2.5 py-1 text-xs font-bold uppercase text-white">
              <span className="size-1.5 rounded-full bg-white" />
              {status}
            </span>
          )}
        </div>
        <h1 className="text-3xl font-bold text-ink">
          {isFetching && !direction ? 'Loading…' : title}
        </h1>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <Meta Icon={DirFieldIcon}>Field Operations</Meta>
          {assigneeNames.length > 0 && (
            <Meta Icon={DdAssigneeIcon}>{assigneeNames.join(', ')}</Meta>
          )}
          {dueDate && <Meta Icon={DirCalendarIcon}>{dayjs(dueDate).format('MMM D, YYYY')}</Meta>}
          {(dueTime || duration) && (
            <Meta Icon={DdClockIcon}>
              {[dueTime, duration && `${duration} hrs`].filter(Boolean).join(' · ')}
            </Meta>
          )}
        </div>
      </div>

      {/* Directive Details */}
      <div className={[CARD, 'flex flex-col gap-6'].join(' ')}>
        <CardTitle Icon={DdDetailsIcon}>Directive Details</CardTitle>

        {direction && dirOneLine(direction) && (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#6b7280]">Objective</span>
            <div className="rounded-r-md border-l-4 border-brand bg-brand-shade px-5 py-4 text-sm leading-[22px] text-ink">
              {dirOneLine(direction)}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#6b7280]">
            Description
          </span>
          <p className="whitespace-pre-line text-sm leading-[22px] text-ink">
            {direction && dirDescription(direction)
              ? dirDescription(direction)
              : 'No description provided.'}
          </p>
        </div>
      </div>

      {/* Influence Person */}
      {rp && (
        <div className={[CARD, 'flex flex-col gap-6'].join(' ')}>
          <CardTitle Icon={DdReferenceIcon}>Influence Person</CardTitle>
          <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
            <InfoField label="Scope">
              {REF_SCOPE_LABEL[String(rp.scope || '').toLowerCase()] || rp.scope || '—'}
            </InfoField>
            <InfoField label="Influence Person">{refPersonLabel}</InfoField>
            <InfoField label="Designation">{rp.designation || '—'}</InfoField>
            {(rp.phone || dirReferencePersonPhone(direction!)) && (
              <InfoField label="Contact Number">
                {rp.phone || dirReferencePersonPhone(direction!)}
              </InfoField>
            )}
            {rp.location && <InfoField label="Location">{refLocationLabel}</InfoField>}
          </div>
        </div>
      )}

      {/* Resources & Attachments */}
      <div className={[CARD, 'flex flex-col gap-6'].join(' ')}>
        <div className="flex items-center justify-between">
          <CardTitle Icon={FolderIcon}>Resources &amp; Attachments</CardTitle>
          <span className="rounded-md bg-[#f3f4f6] px-2 py-1 text-xs font-bold text-[#4b5563]">
            {files.length} {files.length === 1 ? 'File' : 'Files'}
          </span>
        </div>
        {files.length === 0 ? (
          <p className="text-sm text-ink-secondary">No attachments.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {files.map((file) => {
              const { Icon, color } = fileVisual(file);
              const name = attachmentName(file);
              return (
                <div key={file.id} className="overflow-hidden rounded-lg border border-[#e5e7eb] bg-[#f9fafb]">
                  {isImage(file) && file.file_url ? (
                    <a
                      href={file.file_url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`View ${name}`}
                      className="block h-24 bg-[#f8fafc]"
                    >
                      <img src={file.file_url} alt={name} className="size-full object-cover" />
                    </a>
                  ) : (
                    <div className="flex h-24 items-center justify-center bg-[#f8fafc]">
                      <Icon className={['size-12', color].join(' ')} />
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t border-[#e5e7eb] bg-white px-3 py-3">
                    <span className="truncate text-sm font-medium text-ink" title={name}>
                      {name}
                    </span>
                    {file.file_url && (
                      <a
                        href={file.file_url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Download ${name}`}
                        className="shrink-0 text-[#64748b] transition-colors hover:text-brand"
                      >
                        <DownloadIcon className="size-5" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Comments */}
      {id && <CommentsPanel entityId={id} entityType={ENTITY} />}

      {/* Activity History */}
      {id && <ActivityHistoryPanel entityId={id} entityType={ENTITY} />}

      {/* Update Status */}
      <div className={[CARD, 'flex flex-col gap-6'].join(' ')}>
        <CardTitle Icon={RefreshIcon}>Update Status</CardTitle>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1.5">
            <span className="text-sm text-ink">Select new status</span>
            <Select
              className="w-full sm:w-[320px]"
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
            />
          </div>
          <button
            type="button"
            onClick={submitStatus}
            disabled={isSaving}
            className="rounded-lg bg-brand px-8 py-2 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark disabled:opacity-60"
          >
            {isSaving ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
